import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5DtcsResult } from '../types';

import { toDtcsResult } from '../helpers/mappers';

/** Parameters of {@link GetAllDtcs}. */
export type GetAllDtcsParams = {
    /** The Cloud Connector ID of the open session. */
    cloudConnectorId: string;
    /** Called with the progress, from 0 to 100, of each in-progress line of the read. */
    onProgress?: (progress: number) => void;
    /** Aborts the read. */
    signal?: AbortSignal;
};

/**
 * Read every DTC of the vehicle in an open SSM5 session: the backend reads each system through the
 * Cloud Connector, streaming its progress on the way.
 */
export class GetAllDtcs implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(GetAllDtcs, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Run the read and settle it from its last line.
     *
     * @param params - {@link GetAllDtcsParams}
     * @returns what the read found, else whether it can be recovered; rejects when aborted or when the read throws
     */
    async exec({ cloudConnectorId, onProgress, signal }: GetAllDtcsParams): Promise<Ssm5DtcsResult> {
        const lastLine = await this.client.getAllDtcs({ cloudConnectorId }, signal, (line) => {
            if (line.state === 'in-progress') {
                onProgress?.(line.progress);
            }
        });

        return toDtcsResult(lastLine);
    }
}
